import api from '../../api/axiosInstance';
import keycloak from '../../services/keycloak';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  sources?: string[];
  toolCalls?: object[];
  timestamp?: string;
}

export interface ChatRequest {
  message: string;
  sessionId?: string;
}

export interface ChatResponse {
  reply: string;
  sessionId: string;
  sources: string[];
  toolCalls: object[];
  elapsedMs: number;
}

export const aiChatService = {
  sendMessage: async (request: ChatRequest): Promise<ChatResponse> => {
    const response = await api.post('/api/ai/chat', request);
    return response.data;
  },

  sendMessageStream: async (
    request: ChatRequest,
    onToken: (token: string) => void,
    onDone: (metadata: { sources: string[]; toolCalls: object[]; sessionId: string }) => void,
    onError: (error: string) => void,
  ): Promise<void> => {
    try {
      if (keycloak.authenticated) {
        await keycloak.updateToken(5);
      }
      const token = keycloak.token || '';
      const baseUrl = api.defaults.baseURL || '';
      const response = await fetch(`${baseUrl}/api/ai/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        onError('Failed to connect to AI service');
        return;
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        onError('Stream not available');
        return;
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value);
        const lines = text.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.token) {
                onToken(data.token);
              } else if (data.done) {
                onDone({ sources: data.sources || [], toolCalls: data.toolCalls || [], sessionId: data.sessionId || '' });
              } else if (data.error) {
                onError(data.error);
              }
            } catch (e) {
              // skip malformed lines
            }
          }
        }
      }
    } catch (e: any) {
      onError(e.message || 'Stream failed');
    }
  },
};
